import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { BlogFilters as BlogFiltersType, BlogCategory } from '../types';

interface BlogFiltersProps {
  categories: BlogCategory[];
  filters: BlogFiltersType;
  onFilterChange: (filters: BlogFiltersType) => void;
  totalResults: number;
}

const BlogFilters = ({ categories, filters, onFilterChange, totalResults }: BlogFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const sortOptions = [
    { value: 'latest', label: 'Latest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'trending', label: 'Trending' }
  ];

  // Use the categories provided by the parent. Add a default "all" option only if not present
  const categoryOptions = categories && categories.length > 0
    ? (categories.some(c => c.id === 'all')
        ? categories.map(cat => ({ value: cat.id, label: cat.name }))
        : [{ value: 'all', label: 'All Categories' }, ...categories.map(cat => ({ value: cat.id, label: cat.name }))])
    : [{ value: 'all', label: 'All Categories' }];

  const handleSortChange = (value: string | number) => {
    onFilterChange({ ...filters, sortBy: value as 'latest' | 'popular' | 'trending' });
  };

  const handleCategoryChange = (value: string | number) => {
    onFilterChange({ ...filters, category: value as string });
  };

  const clearFilters = () => {
    onFilterChange({
      category: 'all',
      tag: '',
      search: '',
      sortBy: 'latest'
    });
  };

  const hasActiveFilters = filters.category !== 'all' || filters.tag !== '' || filters.search !== '';

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-text-primary">Filters</h3>
          <span className="text-sm text-text-secondary">
            ({totalResults} {totalResults === 1 ? 'article' : 'articles'})
          </span>
        </div>
          <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="lg:hidden p-2 hover:bg-surface rounded-lg transition-colors duration-fast"
        >
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} className="w-5 h-5" />
        </button>
      </div>

      <div className={`space-y-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            name="category"
            label="Category"
            options={categoryOptions}
            value={filters.category}
            onChange={handleCategoryChange}
          />

          <Select
            name="sortBy"
            label="Sort By"
            options={sortOptions}
            value={filters.sortBy}
            onChange={handleSortChange}
          />

          <div className="flex items-end">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2.5 bg-surface hover:bg-muted border border-border rounded-lg text-sm font-medium text-text-primary transition-colors duration-fast flex items-center justify-center space-x-2"
              >
                <Icon name="X" className="w-4 h-4" />
                <span>Clear Filters</span>
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <span className="text-sm text-text-secondary">Active filters:</span>
            {filters.category !== 'all' && (
              <span className="inline-flex items-center space-x-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium">
                <span>{categories.find(c => c.id === filters.category)?.name}</span>
                <button onClick={() => onFilterChange({ ...filters, category: 'all' })}>
                  <Icon name="X" className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.search && (
              <span className="inline-flex items-center space-x-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium">
                <span>Search: {filters.search}</span>
                <button onClick={() => onFilterChange({ ...filters, search: '' })}>
                  <Icon name="X" className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogFilters;